import { supabase } from '../utils/supabase';
import { formatNameForDisplay } from '../utils/nameUtils';
import {
  getDisplayElo,
  getPlayerFullName,
  getSinglesClubEloRating,
} from '../utils/playerUtils';

const SCHEDULE_PLAYER_EMBEDS = `
  *,
  team1_player1:players!scheduled_games_team1_player1_id_fkey(id, first_name, last_name, gender, avatar_url),
  team1_player2:players!scheduled_games_team1_player2_id_fkey(id, first_name, last_name, gender, avatar_url),
  team2_player1:players!scheduled_games_team2_player1_id_fkey(id, first_name, last_name, gender, avatar_url),
  team2_player2:players!scheduled_games_team2_player2_id_fkey(id, first_name, last_name, gender, avatar_url)
`;

const SCHEDULE_TEAM_PLAYER_KEYS = ['team1_player1', 'team1_player2', 'team2_player1', 'team2_player2'];

/** Each scheduled_games embed key and its FK column (for hydration when PostgREST embeds are null). */
const SCHEDULE_PLAYER_EMBED_TO_FK = [
  ['team1_player1', 'team1_player1_id'],
  ['team1_player2', 'team1_player2_id'],
  ['team2_player1', 'team2_player1_id'],
  ['team2_player2', 'team2_player2_id'],
];

function scheduledPlayerEmbedMissingOrNameless(embed, playerId) {
  if (!playerId) return false;
  if (embed == null) return true;
  if (typeof embed !== 'object') return true;
  const fn = String(embed.first_name ?? '').trim();
  const ln = String(embed.last_name ?? '').trim();
  return !fn && !ln;
}

/**
 * Some environments return null/empty player embeds on `scheduled_games` joins (FK hint drift, RLS edge cases).
 * Batch-load `players` by FK id so home + session scheduled lists still show names.
 */
async function hydrateScheduledGamePlayersInRows(rows) {
  if (!rows?.length) return [];
  const need = new Set();
  rows.forEach((row) => {
    SCHEDULE_PLAYER_EMBED_TO_FK.forEach(([embedKey, idKey]) => {
      const pid = row[idKey];
      const emb = row[embedKey];
      if (scheduledPlayerEmbedMissingOrNameless(emb, pid)) need.add(pid);
    });
  });
  const playerIds = [...need];
  if (!playerIds.length) return rows;

  try {
    const { data, error } = await supabase
      .from('players')
      .select('id, first_name, last_name, gender, avatar_url')
      .in('id', playerIds);
    if (error) throw error;
    const byId = {};
    (data || []).forEach((p) => {
      if (p?.id) byId[p.id] = p;
    });

    return rows.map((row) => {
      const next = { ...row };
      SCHEDULE_PLAYER_EMBED_TO_FK.forEach(([embedKey, idKey]) => {
        const pid = row[idKey];
        if (!pid || !scheduledPlayerEmbedMissingOrNameless(row[embedKey], pid)) return;
        const fresh = byId[pid];
        if (!fresh) return;
        const prev = row[embedKey] && typeof row[embedKey] === 'object' ? row[embedKey] : {};
        next[embedKey] = { ...prev, ...fresh };
      });
      return next;
    });
  } catch (e) {
    console.warn('hydrateScheduledGamePlayersInRows:', e);
    return rows;
  }
}

/**
 * Club/session Elo lives on `player_club_elo_ratings` (hosted DBs often have no elo columns on `players`).
 * Merge ratings into nested player shapes so matchup math matches the session UI.
 */
async function mergePlayerClubEloIntoScheduledGameRows(rows, sessionId) {
  if (!rows?.length || !sessionId) return rows || [];

  const { data: sessionRow, error: sessionErr } = await supabase
    .from('sessions')
    .select('season_id')
    .eq('id', sessionId)
    .maybeSingle();

  if (sessionErr) {
    console.warn('mergePlayerClubEloIntoScheduledGameRows: session lookup', sessionErr);
    return rows;
  }

  const seasonId = sessionRow?.season_id;
  const clubId = rows[0]?.club_id;
  if (!clubId || !seasonId) {
    return rows;
  }

  const ids = new Set();
  rows.forEach((row) => {
    SCHEDULE_TEAM_PLAYER_KEYS.forEach((k) => {
      const p = row[k];
      if (p?.id) ids.add(p.id);
    });
  });
  const playerIds = [...ids];
  if (!playerIds.length) return rows;

  const { data: ratings, error: ratingsErr } = await supabase
    .from('player_club_elo_ratings')
    .select('player_id, mixed_elo_rating, levels_elo_rating, singles_elo_rating')
    .eq('club_id', clubId)
    .eq('season_id', seasonId)
    .in('player_id', playerIds);

  if (ratingsErr) {
    console.warn('mergePlayerClubEloIntoScheduledGameRows: elo lookup', ratingsErr);
    return rows;
  }

  const byPid = {};
  for (const r of ratings || []) byPid[r.player_id] = r;

  const patchEmbed = (embed) => {
    if (!embed?.id) return embed;
    const elo = byPid[embed.id];
    if (!elo) return embed;
    return {
      ...embed,
      mixed_elo_rating: elo.mixed_elo_rating,
      levels_elo_rating: elo.levels_elo_rating,
      singles_elo_rating: elo.singles_elo_rating,
    };
  };

  return rows.map((row) => ({
    ...row,
    team1_player1: patchEmbed(row.team1_player1),
    team1_player2: patchEmbed(row.team1_player2),
    team2_player1: patchEmbed(row.team2_player1),
    team2_player2: patchEmbed(row.team2_player2),
  }));
}

/**
 * Fetch scheduled games for a session that are still relevant to the sidebar.
 * Returns pending and on_court games (not completed — those are kept in DB for
 * history only and are not shown during an active session).
 */
export const fetchScheduledGames = async (sessionId) => {
  try {
    const { data, error } = await supabase
      .from('scheduled_games')
      .select(SCHEDULE_PLAYER_EMBEDS)
      .eq('session_id', sessionId)
      .in('status', ['pending', 'placed'])
      .order('order_index', { ascending: true });

    if (error) throw error;
    const raw = data || [];
    const hydrated = await hydrateScheduledGamePlayersInRows(raw);
    const merged = await mergePlayerClubEloIntoScheduledGameRows(hydrated, sessionId);
    return merged;
  } catch (error) {
    console.error('Error fetching scheduled games:', error);
    throw error;
  }
};

/** Short label for teaser (matches session UI density). */
const playerTeaserLabel = (p) => {
  if (!p) return '';
  const first = String(p.first_name || '').trim();
  const last = String(p.last_name || '').trim();
  if (!first && !last) return '';
  if (!last) return first;
  return `${first} ${last[0].toUpperCase()}.`;
};

/** "Team vs Team" for scheduled_games embed shape from fetchScheduledGames. */
const formatScheduledGameMatchup = (row) => {
  const t1 = [playerTeaserLabel(row.team1_player1), playerTeaserLabel(row.team1_player2)].filter(Boolean);
  const t2 = [playerTeaserLabel(row.team2_player1), playerTeaserLabel(row.team2_player2)].filter(Boolean);
  const left = t1.length ? t1.join(' · ') : 'TBD';
  const right = t2.length ? t2.join(' · ') : 'TBD';
  return `${left} vs ${right}`;
};

/** Doubles-only: average rating for team badges (mirror session Upcoming modal). */
const scheduledDoublesTeamAvgEloFromEmbeds = (p1, p2) => {
  if (!p1 || !p2) {
    const one = p1 || p2;
    return one ? one.levels_elo_rating || 0 : 0;
  }
  const p1Elo =
    p1.gender && p2.gender && p1.gender !== p2.gender
      ? p1.mixed_elo_rating || 0
      : p1.levels_elo_rating || 0;
  const p2Elo =
    p1.gender && p2.gender && p1.gender !== p2.gender
      ? p2.mixed_elo_rating || 0
      : p2.levels_elo_rating || 0;
  return Math.round((p1Elo + p2Elo) / 2);
};

const mapEmbedToSlotPreview = (embed, teammate, useSingles) => {
  if (!embed) return { empty: true };
  const fullName = getPlayerFullName(embed).trim();
  const teaser = playerTeaserLabel(embed);
  const displaySource = fullName || teaser || 'Player';
  const { display: compactName } = formatNameForDisplay(displaySource, {
    maxLength: 22,
    useInitials: false,
  });
  return {
    id: embed.id,
    gender: embed.gender,
    avatar_url: embed.avatar_url,
    name: compactName || displaySource,
    fullName: fullName || displaySource,
    displayElo: getDisplayElo(embed, teammate, useSingles),
  };
};

/**
 * Match preview blocks for Home / modals — same math as scheduled_games row,
 * keyed by nullable player embeds per slot (next_up/active court shape).
 *
 * @param {Array<object|null|undefined>} team1EmbedSlots
 * @param {Array<object|null|undefined>} team2EmbedSlots
 * @param {1|2} playersPerSide
 */
export function buildTeamsPreviewFromCourtEmbeddings(team1EmbedSlots, team2EmbedSlots, playersPerSide) {
  const ps = playersPerSide === 2 ? 2 : 1;
  const pad = (slots) => {
    const arr = [...(slots || [])];
    while (arr.length < ps) arr.push(null);
    return arr.slice(0, ps);
  };

  const t1 = pad(team1EmbedSlots);
  const t2 = pad(team2EmbedSlots);
  const useSingles = ps === 1;

  const mapSide = (embeds) =>
    embeds.map((embed, idx) => {
      if (!embed) return { empty: true };
      const teammate = useSingles
        ? null
        : embeds.find((e, i) => e && i !== idx) || null;
      return mapEmbedToSlotPreview(embed, teammate, useSingles);
    });

  const team1Players = mapSide(t1);
  const team2Players = mapSide(t2);

  let team1Avg;
  let team2Avg;
  if (useSingles) {
    const s1 = getSinglesClubEloRating(t1[0]);
    const s2 = getSinglesClubEloRating(t2[0]);
    team1Avg = s1 == null ? 0 : s1;
    team2Avg = s2 == null ? 0 : s2;
  } else {
    team1Avg = scheduledDoublesTeamAvgEloFromEmbeds(t1[0], t1[1]);
    team2Avg = scheduledDoublesTeamAvgEloFromEmbeds(t2[0], t2[1]);
  }

  return {
    playersPerSide: ps,
    teams: [
      { teamNumber: 1, avgElo: team1Avg, players: team1Players },
      { teamNumber: 2, avgElo: team2Avg, players: team2Players },
    ],
  };
}

export function formatCourtEmbeddingsMatchup(team1EmbedSlots, team2EmbedSlots, playersPerSide) {
  const ps = playersPerSide === 2 ? 2 : 1;
  const pad = (slots) => {
    const arr = [...(slots || [])];
    while (arr.length < ps) arr.push(null);
    return arr.slice(0, ps);
  };
  const left = pad(team1EmbedSlots).map((p) => playerTeaserLabel(p)).filter(Boolean);
  const right = pad(team2EmbedSlots).map((p) => playerTeaserLabel(p)).filter(Boolean);
  const lStr = left.length ? left.join(' · ') : 'TBD';
  const rStr = right.length ? right.join(' · ') : 'TBD';
  return `${lStr} vs ${rStr}`;
}

const buildTeamsPreviewFromScheduledRow = (row) => {
  const isSingles =
    row.team1_player2_id == null &&
    row.team2_player2_id == null &&
    row.team1_player1_id &&
    row.team2_player1_id;
  if (isSingles) {
    return buildTeamsPreviewFromCourtEmbeddings(
      [row.team1_player1],
      [row.team2_player1],
      1
    );
  }
  return buildTeamsPreviewFromCourtEmbeddings(
    [row.team1_player1, row.team1_player2],
    [row.team2_player1, row.team2_player2],
    2
  );
};

/**
 * Primary row for Home "live session" teaser: prefers on-court, else earliest pending.
 * Works only for competitive-style sessions that use scheduled_games; otherwise null.
 *
 * @returns {Promise<{
 *   kind: 'on_court'|'up_next',
 *   matchup: string,
 *   previewHeading: string,
 *   previewSubheading: string|null,
 *   teams: Array<{ teamNumber: number, avgElo: number, players: Array<object> }>,
 *   playersPerSide: 1|2
 * } | null>}
 */
export const getLiveSessionGameHighlight = async (sessionId) => {
  try {
    const rows = await fetchScheduledGames(sessionId);
    if (!rows?.length) return null;
    const placed = rows.filter((r) => r.status === 'placed');
    const pending = rows.filter((r) => r.status === 'pending');
    let row;
    let kind;
    if (placed.length) {
      row = placed.reduce((a, b) => (a.order_index <= b.order_index ? a : b));
      kind = 'on_court'; // kept for HomeNextSession display contract
    } else if (pending.length) {
      row = pending.reduce((a, b) => (a.order_index <= b.order_index ? a : b));
      kind = 'up_next';
    } else {
      return null;
    }
    const { teams, playersPerSide } = buildTeamsPreviewFromScheduledRow(row);

    return {
      kind,
      matchup: formatScheduledGameMatchup(row),
      previewHeading: 'Next on Court',
      previewSubheading: null,
      teams,
      playersPerSide,
    };
  } catch (e) {
    console.warn('getLiveSessionGameHighlight:', sessionId, e);
    return null;
  }
};

/**
 * The next pending/on_court scheduled game (lowest order_index among active rows).
 */
export const getNextScheduledGame = async (sessionId) => {
  try {
    const { data, error } = await supabase
      .from('scheduled_games')
      .select('*')
      .eq('session_id', sessionId)
      .in('status', ['pending', 'placed'])
      .order('order_index', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') throw error;
    return data ?? null;
  } catch (error) {
    console.error('Error fetching next scheduled game:', error);
    throw error;
  }
};

const rosterPlayerIdsFromScheduledGame = (game) => {
  if (Array.isArray(game?.roster_player_ids) && game.roster_player_ids.length > 0) {
    return game.roster_player_ids;
  }
  return [
    game?.team1_player1_id,
    game?.team1_player2_id,
    game?.team2_player1_id,
    game?.team2_player2_id,
  ].filter(Boolean);
};

/**
 * Create a new scheduled game via create_scheduled_game RPC.
 * Doubles: 4 distinct players (team1 x2, team2 x2). Singles: 2 players (nullable team*_player2 columns).
 */
export const createScheduledGame = async (sessionId, clubId, team1, team2, createdByPlayerId) => {
  try {
    const t1 = (team1 || []).filter(Boolean);
    const t2 = (team2 || []).filter(Boolean);
    const isSingles = t1.length === 1 && t2.length === 1;
    const isDoubles = t1.length === 2 && t2.length === 2;
    if (!isSingles && !isDoubles) {
      throw new Error('Teams must be 1+1 for singles or 2+2 for doubles');
    }

    const playerIds = [...t1, ...t2];
    const expected = isSingles ? 2 : 4;
    if (playerIds.length !== expected || new Set(playerIds).size !== expected) {
      throw new Error(isSingles ? 'Singles scheduled games need two different players' : 'Must select exactly 4 distinct players');
    }

    const { data: existingGames, error: checkError } = await supabase
      .from('scheduled_games')
      .select('id, roster_player_ids, team1_player1_id, team1_player2_id, team2_player1_id, team2_player2_id')
      .eq('session_id', sessionId)
      .in('status', ['pending', 'placed']);

    if (checkError) throw checkError;

    const conflictingPlayers = [];
    for (const game of existingGames || []) {
      const roster = rosterPlayerIdsFromScheduledGame(game);
      for (const pid of playerIds) {
        if (roster.includes(pid) && !conflictingPlayers.includes(pid)) {
          conflictingPlayers.push(pid);
        }
      }
    }

    if (conflictingPlayers.length > 0) {
      throw new Error(`Players already in scheduled games: ${conflictingPlayers.join(', ')}`);
    }

    const { data: rpcResult, error: rpcError } = await supabase.rpc('create_scheduled_game', {
      p_session_id: sessionId,
      p_club_id: clubId,
      p_team1_player1_id: t1[0],
      p_team2_player1_id: t2[0],
      p_team1_player2_id: isSingles ? null : t1[1],
      p_team2_player2_id: isSingles ? null : t2[1],
      p_order_index: null,
      p_created_by: createdByPlayerId,
    });

    if (rpcError) throw rpcError;
    if (!rpcResult?.ok) {
      throw new Error(rpcResult?.message || 'Failed to create scheduled game');
    }

    const { data: game, error: fetchError } = await supabase
      .from('scheduled_games')
      .select(SCHEDULE_PLAYER_EMBEDS)
      .eq('id', rpcResult.id)
      .single();

    if (fetchError) throw fetchError;
    const [hydrated] = await hydrateScheduledGamePlayersInRows([game]);
    const [merged] = await mergePlayerClubEloIntoScheduledGameRows([hydrated], sessionId);
    return merged;
  } catch (error) {
    console.error('Error creating scheduled game:', error);
    throw error;
  }
};

/**
 * Delete a scheduled game (host explicit removal).
 * Use only for manual host-initiated removal, not for consumption.
 */
export const deleteScheduledGame = async (scheduledGameId) => {
  try {
    const { error } = await supabase
      .from('scheduled_games')
      .delete()
      .eq('id', scheduledGameId);

    if (error) throw error;

  } catch (error) {
    console.error('Error deleting scheduled game:', error);
    throw error;
  }
};

/**
 * Cancel a scheduled game via server RPC (handles placed courts + waitlist restore).
 * @param {string} scheduledGameId
 * @param {string|null} tabInstanceId - writer lease tab id when applicable
 */
export const cancelScheduledGame = async (scheduledGameId, tabInstanceId = null) => {
  try {
    const { data, error } = await supabase.rpc('cancel_scheduled_game', {
      p_scheduled_game_id: scheduledGameId,
      p_tab_instance_id: tabInstanceId,
    });

    if (error) throw error;
    if (!data?.ok) {
      throw new Error(data?.message || 'Failed to cancel scheduled game');
    }

    return data;
  } catch (error) {
    console.error('Error cancelling scheduled game:', error);
    throw error;
  }
};

/**
 * Update a scheduled game's status.
 *
 * 'placed' — lifecycle is DB-owned (place_scheduled_game RPC, finish_session_match).
 *            Client must not set status=placed without placed_court_id.
 * 'completed' — called when the court's score is submitted and the game is finished.
 *
 * @param {string} scheduledGameId
 * @param {'pending'|'placed'|'completed'} status
 * @returns {Promise<{ success: boolean, conflict?: boolean }>}
 */
export const updateScheduledGameStatus = async (scheduledGameId, status) => {
  try {
    if (status === 'placed') {
      // chk_placed_requires_court: placed rows must have placed_court_id (set by DB RPCs).
      return { success: true };
    }

    const { error } = await supabase
      .from('scheduled_games')
      .update({ status })
      .eq('id', scheduledGameId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error updating scheduled game status:', error);
    throw error;
  }
};

/**
 * Update order_index for multiple games (for drag-and-drop reordering)
 */
export const updateScheduledGameOrder = async (sessionId, reorderedGames) => {
  try {
    // Update each game's order_index
    const updates = reorderedGames.map((game, index) => 
      supabase
        .from('scheduled_games')
        .update({ order_index: index })
        .eq('id', game.id)
    );

    const results = await Promise.all(updates);
    
    // Check for errors
    for (const result of results) {
      if (result.error) throw result.error;
    }

    console.log('✅ Scheduled games reordered');
  } catch (error) {
    console.error('Error updating scheduled game order:', error);
    throw error;
  }
};

/**
 * Validate if a scheduled game is still valid (all players exist and in session)
 */
export const validateScheduledGame = async (scheduledGame, sessionPlayerStatus) => {
  const playerIds = [
    scheduledGame.team1_player1_id,
    scheduledGame.team1_player2_id,
    scheduledGame.team2_player1_id,
    scheduledGame.team2_player2_id
  ].filter(Boolean);

  const validPlayers = playerIds.filter(playerId => {
    const status = sessionPlayerStatus[playerId];
    return status === 'active' || status === 'signed_up' || status === 'paused';
  });

  return {
    isValid: validPlayers.length === 4,
    validPlayerCount: validPlayers.length,
    invalidPlayers: playerIds.filter(id => !validPlayers.includes(id))
  };
};

/**
 * No-op: session_players.scheduled_game_id was dropped in migration 109.
 * Placement is now tracked exclusively via session_courts.scheduled_game_id.
 */
export const clearScheduledGamePlayerFlags = async (_sessionId, _scheduledGameId) => {
  // Column no longer exists — nothing to clear.
};

/**
 * No-op: session_players.scheduled_game_id was dropped in migration 109.
 */
export const clearPlayerScheduledFlag = async (_playerId, _sessionId) => {
  // Column no longer exists — nothing to clear.
};

/**
 * No-op: session_players.scheduled_game_id was dropped in migration 109.
 */
export const updatePlayerScheduledFlag = async (_playerId, _scheduledGameId, _sessionId) => {
  // Column no longer exists — nothing to update.
};

function isUniqueViolation(error) {
  return error?.code === '23505';
}

/**
 * Replay a queued scheduled-game create/cancel using the same client UUID.
 */
export async function flushScheduledGameFromOfflineCommand(command, { sessionId, clubId }) {
  if (command?.action === 'cancel') {
    if (!command.scheduledGameId) {
      throw new Error('Missing scheduled game id');
    }
    try {
      await cancelScheduledGame(command.scheduledGameId);
    } catch (error) {
      const message = String(error?.message || '');
      if (/not found|already|SESSION_NOT_FOUND|GAME_NOT_FOUND/i.test(message)) {
        return { ok: true, alreadyCancelled: true };
      }
      throw error;
    }
    return { ok: true };
  }

  if (!sessionId || !clubId) {
    throw new Error('Missing session or club');
  }
  const scheduledGameId = command?.scheduledGameId;
  if (!scheduledGameId) {
    throw new Error('Missing scheduled game id');
  }

  const { data: existing, error: lookupError } = await supabase
    .from('scheduled_games')
    .select('id')
    .eq('id', scheduledGameId)
    .maybeSingle();
  if (lookupError) throw lookupError;
  if (existing) return { ok: true, alreadyApplied: true };

  const team1 = (command.team1 || []).filter(Boolean);
  const team2 = (command.team2 || []).filter(Boolean);
  const roster = [...team1, ...team2];
  const { data: lastOrderRow, error: orderError } = await supabase
    .from('scheduled_games')
    .select('order_index')
    .eq('session_id', sessionId)
    .order('order_index', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (orderError) throw orderError;
  const nextOrderIndex = (lastOrderRow?.order_index ?? -1) + 1;
  const { error: insertError } = await supabase.from('scheduled_games').insert({
    id: scheduledGameId,
    session_id: sessionId,
    club_id: clubId,
    team1_player1_id: team1[0] || null,
    team1_player2_id: team1[1] || null,
    team2_player1_id: team2[0] || null,
    team2_player2_id: team2[1] || null,
    order_index: nextOrderIndex,
    created_by: command.createdBy || null,
    roster_size_expected: roster.length,
    roster_player_ids: roster,
    status: 'pending',
  });
  if (insertError && !isUniqueViolation(insertError)) {
    throw insertError;
  }
  return { ok: true };
}
